import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import type { ReactNode } from 'react';
import { createContext, useEffect, useMemo, useState } from 'react';
import authConfig from 'src/configs/auth';
import axiosInstance from 'src/configs/axios';

import { KEEP_ON_NAVIGATE, notify } from '@/components/ui/notify';
import { LOCAL_STORAGE_IV, LOCAL_STORAGE_USER } from '@/constants/local-storage';
import { endSession } from '@/services/session';
import { toApiErrorInfo } from '@/utils/api-error-i18n';
import { clickIdBody } from '@/utils/click-id';
import { openAiAdsClickRefBody } from '@/utils/openai-ads-click-ref';
import { reportOpenAiAdsConversion } from '@/utils/openai-ads-conversions';
import { getPostLoginRedirectPath } from '@/utils/post-login-redirect';
import { getSsoUtmData, getUtmData } from '@/utils/user-tracking-data';

import type { AuthValuesType, ConfirmationCallbackType, CreateUserParams, CreateUserTrialParams, DiscordLoginParams, ErrCallbackType, GoogleLoginParams, LoginOptions, LoginParams, SocialAccount, SsoLoginResult, SsoProviderName, UserDataType } from './types';

const SOFTWARE_LOGIN_TOKEN_STORAGE_KEY = 'noping_login_token';

const encoder = new TextEncoder();
const decoder = new TextDecoder();

async function getKey(password: string | undefined) {
  const baseKey = await crypto.subtle.importKey('raw', encoder.encode(password), { name: 'PBKDF2' }, false, ['deriveBits', 'deriveKey']);
  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: encoder.encode('pick-a-salt'),
      iterations: 100000,
      hash: 'SHA-256',
    },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt']
  );
}

async function encryptData(data: UserDataType, password: string) {
  const key = await getKey(password);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const alg = { name: 'AES-GCM', iv };
  const encrypted = await crypto.subtle.encrypt(alg, key, encoder.encode(JSON.stringify(data)));
  return {
    encrypted: Array.from(new Uint8Array(encrypted)),
    iv: Array.from(iv),
  };
}

async function decryptData(encryptedData: Iterable<number>, iv: Iterable<number>, password: string) {
  const key = await getKey(password);
  const alg = { name: 'AES-GCM', iv: new Uint8Array(iv) };
  const decrypted = await crypto.subtle.decrypt(alg, key, new Uint8Array(encryptedData));
  return JSON.parse(decoder.decode(decrypted));
}

function ssoLoginResultOf(responseData: { data?: { is_new_account?: unknown } } | undefined, email: string | null): SsoLoginResult {
  return { isNewAccount: responseData?.data?.is_new_account === true, email };
}

const defaultProvider: AuthValuesType = {
  user: null,
  loading: true,
  setUser: () => null,
  setLoading: () => Boolean,
  login: () => Promise.resolve(),
  loginWithGoogle: () => Promise.resolve(),
  loginWithDiscord: () => Promise.resolve(),
  logout: () => Promise.resolve(false),
  create: () => Promise.resolve(),
  me: () => Promise.resolve(),
  createTrial: () => Promise.resolve(),
  getSocialAccounts: () => Promise.resolve([]),
  linkGoogle: () => Promise.resolve(),
  linkDiscord: () => Promise.resolve(),
  unlinkProvider: () => Promise.resolve(),
};

const AuthContext = createContext<AuthValuesType>(defaultProvider);

type Props = {
  children: ReactNode;
};

const AuthProvider = ({ children }: Props) => {
  const [user, setUser] = useState<UserDataType | null>(defaultProvider.user);
  const [loading, setLoading] = useState<boolean>(defaultProvider.loading);
  const router = useRouter();
  const { t } = useTranslation('common');

  const encryptionKey = 'bcE8BaWNUKBxDUlOZQdW7KdGNcg6lRax4ONw26geqfQ=';

  // The API session lives in a cookie, so clearing the browser alone does not end it: the browser only
  // forgets the user once the server confirms the session is gone. The software login token goes too,
  // because `/login/token` still exchanges it for a session until it is redeemed.
  const handleLogout = async (): Promise<boolean> => {
    try {
      await endSession();
    } catch {
      notify.error(t('login.logout_failed'));
      return false;
    }
    localStorage.removeItem(LOCAL_STORAGE_USER);
    localStorage.removeItem(LOCAL_STORAGE_IV);
    localStorage.removeItem(SOFTWARE_LOGIN_TOKEN_STORAGE_KEY);
    setUser(null);
    if (router.pathname.includes('/dashboard/')) {
      router.replace('/');
    }
    return true;
  };

  // Both effects below await a key derivation of 100.000 PBKDF2 iterations, which routinely outlives
  // the render that started it, so each one has to be able to give up: a run that lost its turn must
  // not touch STORAGE. Dropping that guard costs two different things — a stale write that clobbers a
  // newer user (the save effect re-runs on every `user` change, and the slower run would land last),
  // and, under Jest, a write against a jsdom already torn down, which surfaces as `_origin of null`
  // in whatever unrelated suite happens to be running in that worker.
  //
  // The `setUser` below is deliberately OUTSIDE that rule, and it is not an oversight. Under
  // StrictMode (`next.config.mjs`, so every `next dev` and every e2e run) the effects are
  // double-invoked, and the save effect's `else` branch — which has no await before it — wipes both
  // keys synchronously on the first pass, while `user` is still null. By the time the second pass of
  // this effect reads storage there is nothing left to decrypt, so the only run that still holds the
  // stored session is the first one, the cancelled one. Guarding its `setUser` drops the session for
  // good: `encryptionKey` is a constant, the deps never change again, and on /pricing nothing else
  // calls `setUser` — the visitor simply stays anonymous (SCRUM-1760's three red e2e). Setting state
  // on an unmounted provider is a no-op in React 18, so this costs nothing the guard was buying.
  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      const encryptedUser = localStorage.getItem(LOCAL_STORAGE_USER);
      const iv = localStorage.getItem(LOCAL_STORAGE_IV);
      // No consent check here (SCRUM-1954): the session is strictly necessary storage. This used to log
      // out anyone without `npCookiesAccepted`, which tied login to the cookie banner — and is why the
      // squad invite wrote consent on the visitor's behalf.

      if (encryptedUser && iv && encryptionKey) {
        try {
          const decryptedUser = await decryptData(JSON.parse(encryptedUser), JSON.parse(iv), encryptionKey);
          setUser(decryptedUser);
        } catch (error) {
          if (cancelled) return;
          localStorage.removeItem(LOCAL_STORAGE_USER);
          localStorage.removeItem(LOCAL_STORAGE_IV);
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [encryptionKey]);

  useEffect(() => {
    let cancelled = false;

    const saveData = async () => {
      if (user && encryptionKey) {
        const { encrypted, iv } = await encryptData(user, encryptionKey);
        if (cancelled) return;
        localStorage.setItem(LOCAL_STORAGE_USER, JSON.stringify(encrypted));
        localStorage.setItem(LOCAL_STORAGE_IV, JSON.stringify(iv));
      } else {
        localStorage.removeItem(LOCAL_STORAGE_USER);
        localStorage.removeItem(LOCAL_STORAGE_IV);
      }
    };

    saveData();

    return () => {
      cancelled = true;
    };
  }, [user, encryptionKey]);

  // Resolves to the e-mail /user/me answered, or null when it failed. The SSO handlers need it because
  // their login response carries no e-mail, and the state set here is not visible to a callback that
  // closed over the previous render.
  const initAuth = async ({ redirect = true }: LoginOptions = {}): Promise<string | null> => {
    setLoading(true);
    try {
      const trackingData = getUtmData();

      const response = await axiosInstance.get(authConfig.meEndpoint, {
        params: trackingData,
      });
      if (response.data && response.data.data && response.data.data.user) {
        setUser({
          id: response.data.data.user.id,
          name: response.data.data.user.nome,
          email: response.data.data.user.usuario,
          isSsoOnly: Boolean(response.data.data.user.is_sso_only),
          vipTime: response.data.data.user.vip_time,
          trialSubDate: response.data.data.user.trial_sub_date,
          daysPlan: response.data.data.user?.days,
          customTrial: response.data.data.custom_trial,
          cellphone: response.data.data.user.telefone,
          country: response.data.data.user.country,
          favGame: response.data.data.user.fav_game,
          addressNumber: response.data.data.user.address_number,
          address: response.data.data.user.address_address,
          addressState: response.data.data.user.address_state,
          addressCep: response.data.data.user.address_cep,
          addressZip: response.data.data.user.address_zip,
        });

        if (redirect) {
          const redirectPath = getPostLoginRedirectPath(router.pathname);
          if (redirectPath) router.push(redirectPath);
        }
      } else {
        throw new Error('User data is not available');
      }
      return response.data.data.user.usuario ?? null;
    } catch {
      setUser(null);
      // The thrown text above is a developer breadcrumb, and an axios rejection carries either
      // its own English string or an API message in the request IP's language. Neither is copy,
      // so the person always reads our own translated line.
      notify.error(t('login.mensage_error_authenticate'));
      return null;
    } finally {
      setLoading(false);
    }
  };

  const me = async () => {
    setLoading(true);
    try {
      const trackingData = getUtmData();
      const response = await axiosInstance.get(authConfig.meEndpoint, {
        params: trackingData,
      });
      if (response.data && response.data.data && response.data.data.user) {
        setUser({
          id: response.data.data.user.id,
          name: response.data.data.user.nome,
          email: response.data.data.user.usuario,
          isSsoOnly: Boolean(response.data.data.user.is_sso_only),
          vipTime: response.data.data.user.vip_time,
          trialSubDate: response.data.data.user.trial_sub_date,
          daysPlan: response.data.data.user?.days,
          customTrial: response.data.data.custom_trial,
          cellphone: response.data.data.user.telefone,
          country: response.data.data.user.country,
          favGame: response.data.data.user.fav_game,
          addressNumber: response.data.data.user.address_number,
          address: response.data.data.user.address_address,
          addressState: response.data.data.user.address_state,
          addressCep: response.data.data.user.address_cep,
          addressZip: response.data.data.user.address_zip,
        });
      }
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (params: LoginParams, errorCallback?: ErrCallbackType, successCallback?: ConfirmationCallbackType, options?: LoginOptions) => {
    setLoading(true);
    axiosInstance
      .post(authConfig.loginEndpoint, params)
      .then(async (response) => {
        if (response.data && response.data.success) {
          await initAuth(options);
          if (successCallback) successCallback();
        }
      })
      .catch((err) => {
        setLoading(false);
        if (errorCallback) errorCallback(toApiErrorInfo(err));
      });
  };

  const persistSoftwareLoginToken = (token: string | undefined) => {
    if (!token || typeof window === 'undefined') return;
    try {
      localStorage.setItem(SOFTWARE_LOGIN_TOKEN_STORAGE_KEY, token);
    } catch {
      // localStorage pode estar indisponível (modo privado, quota). Silenciamos pois o login por cookie já foi bem-sucedido.
    }
  };

  // One body for both SSO providers, so Google and Discord signups can never record different attribution.
  // `sourceUrl` is for the Discord callback: its own URL is not the page the visitor signed up from, so it passes the one that started the OAuth round-trip.
  const ssoBody = <T extends object>(params: T, sourceUrl?: string) => ({ lang: router.locale ?? 'en', ...getSsoUtmData(), ...clickIdBody(), ...openAiAdsClickRefBody(sourceUrl), ...params });

  const handleLoginWithGoogle = (params: GoogleLoginParams, errorCallback?: ErrCallbackType, successCallback?: ConfirmationCallbackType) => {
    setLoading(true);
    axiosInstance
      .post(authConfig.loginGoogleEndpoint, ssoBody(params))
      .then(async (response) => {
        // A 2xx whose body is not `success` used to fall through with NO callback at all: the caller's
        // spinner stayed up forever and nothing ever reached the user. The SSO entry pages
        // (/auth/desktop, /auth/discord/callback) have no other way out — they only learn the outcome
        // through these two callbacks, so every settled response has to pick one.
        if (!response.data?.success) {
          setLoading(false);
          if (errorCallback) errorCallback({ status: response.status, message: response.data?.message });
          return;
        }
        const loginToken = response.data?.data?.token;
        persistSoftwareLoginToken(loginToken);
        // Aguarda /user/me resolver antes de liberar o form — evita gap entre
        // o SSO devolver sucesso e o usuário de fato estar carregado no state.
        const email = await initAuth();
        if (successCallback) successCallback(loginToken, ssoLoginResultOf(response.data, email));
      })
      .catch((err) => {
        setLoading(false);
        if (errorCallback) errorCallback(toApiErrorInfo(err));
      });
  };

  const handleLoginWithDiscord = (params: DiscordLoginParams, errorCallback?: ErrCallbackType, successCallback?: ConfirmationCallbackType, sourceUrl?: string) => {
    setLoading(true);
    axiosInstance
      .post(authConfig.loginDiscordEndpoint, ssoBody(params, sourceUrl))
      .then(async (response) => {
        // Same contract as handleLoginWithGoogle above: a settled response always calls back, or the
        // desktop/mobile handoff sits on its spinner with nothing to show and nothing to retry.
        if (!response.data?.success) {
          setLoading(false);
          if (errorCallback) errorCallback({ status: response.status, message: response.data?.message });
          return;
        }
        const loginToken = response.data?.data?.token;
        persistSoftwareLoginToken(loginToken);
        const email = await initAuth();
        if (successCallback) successCallback(loginToken, ssoLoginResultOf(response.data, email));
      })
      .catch((err) => {
        setLoading(false);
        if (errorCallback) errorCallback(toApiErrorInfo(err));
      });
  };

  // The partner click_id and the OpenAI Ads click reference are merged here, and not at the ~9 call sites
  // of create/createTrial, so a new registration screen cannot ship without them: the campaign trial would
  // then depend on which form the visitor happened to use. Callers spread getUtmData(), whose key is
  // camelCase `clickId` and is therefore not the one the API reads.
  const handleCreate = (params: CreateUserParams, errorCallback?: ErrCallbackType, successCallback?: ConfirmationCallbackType) => {
    axiosInstance
      .post(authConfig.registerEndpoint, { ...clickIdBody(), ...openAiAdsClickRefBody(), ...params })
      .then((response) => {
        if (response.data.success) {
          reportOpenAiAdsConversion({ type: 'registration_completed', email: params.email });
          if (successCallback) successCallback();
        } else {
          throw new Error('User creation failed');
        }
      })
      .catch((err) => {
        if (errorCallback) errorCallback(toApiErrorInfo(err));
      });
  };

  const handleCreateTrial = (params: CreateUserTrialParams, errorCallback?: ErrCallbackType, successCallback?: ConfirmationCallbackType, showSuccessToast: boolean = true) => {
    axiosInstance
      .post(authConfig.registerTrialEndpoint, { lang: router.locale ?? 'en', ...clickIdBody(), ...openAiAdsClickRefBody(), ...params })
      .then((response) => {
        if (response.data.success) {
          // The one place in the front end that knows a trial was actually granted: nine LPs call
          // `createTrial`, and every one of them reaches this branch only when the API answered
          // `success`. Reporting from here instead of from each LP is what keeps "opened the page" and
          // "clicked the button" out of the conversion count (requirement 7 of SCRUM-1965).
          reportOpenAiAdsConversion({ type: 'trial_started', planId: params.custom ?? null, email: params.email ?? null });
          if (showSuccessToast) notify.success(t('login.mensage_user_created_successfully'), KEEP_ON_NAVIGATE);
          if (successCallback) successCallback();
        } else {
          throw new Error('User creation failed');
        }
      })
      .catch((err) => {
        // Every trial signup failure reaches the caller — nothing here may swallow one. That matters for
        // the two rejections the API added with the `exists` validation: a `custom` from another
        // country's campaign answers 400 "Custom Trial is not valid", and an id that no longer exists
        // answers a validation error. Neither literal is in api-error-i18n's table, so both render the
        // form's fallback key instead of the API's English text, but the pop-up still shows.
        if (errorCallback) errorCallback(toApiErrorInfo(err));
      });
  };

  // Endpoints usados pela page de connections (src/pages/dashboard/connections.tsx) pra listar,
  // vincular e desvincular provedores SSO da conta já autenticada. Diferente dos handlers de
  // login acima (callback-style), estes retornam Promise pra a UI poder aguardar e reagir a
  // erros específicos (409 = provider já vinculado a outra conta, 422 last_auth_method = removeria
  // o único método de login do usuário).
  const getSocialAccounts = async (): Promise<SocialAccount[]> => {
    const response = await axiosInstance.get(authConfig.socialAccountsEndpoint);
    return response.data?.data ?? [];
  };

  const linkGoogle = async (token: string): Promise<void> => {
    await axiosInstance.post(`${authConfig.socialAccountsEndpoint}/google`, { token });
  };

  const linkDiscord = async (code: string, redirectUri: string): Promise<void> => {
    await axiosInstance.post(`${authConfig.socialAccountsEndpoint}/discord`, { code, redirect_uri: redirectUri });
  };

  const unlinkProvider = async (provider: SsoProviderName): Promise<void> => {
    await axiosInstance.delete(`${authConfig.socialAccountsEndpoint}/${provider}`);
  };

  const values = useMemo(
    () => ({
      user,
      loading,
      setUser,
      setLoading,
      login: handleLogin,
      loginWithGoogle: handleLoginWithGoogle,
      loginWithDiscord: handleLoginWithDiscord,
      logout: handleLogout,
      create: handleCreate,
      createTrial: handleCreateTrial,
      me,
      getSocialAccounts,
      linkGoogle,
      linkDiscord,
      unlinkProvider,
    }),
    [user, loading, handleLogin, handleLoginWithGoogle, handleLoginWithDiscord, handleLogout, handleCreate, me, handleCreateTrial, getSocialAccounts, linkGoogle, linkDiscord, unlinkProvider]
  );

  return <AuthContext.Provider value={values}>{children}</AuthContext.Provider>;
};

export { AuthContext, AuthProvider };
